import React, { useEffect, useContext, useState, useCallback, useRef } from 'react';
import { Table, Button, Row, Col, Form, Spinner } from 'react-bootstrap';
import * as XLSX from 'xlsx-js-style';
import { MainContext } from '../App';
import apiService from '../api';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faCircleXmark, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';

const ResponsiveTable = styled(Table)`
  @media (max-width: 768px) {
    font-size: 0.7rem;
  }
  table-layout: fixed;
  width: 100%;
`;

const TableHeader = styled.th`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 0.8rem;
  vertical-align: middle !important;
  padding: 0.5rem !important;

  &.route-name {
    min-width: 150px;
    width: 150px;
  }

  &.plate {
    min-width: 100px;
    width: 100px;
  }

  &.document-status {
    min-width: 90px;
    width: 90px;
    text-align: center;
  }
`;

const TableCell = styled.td`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle !important;
  padding: 0.5rem !important;

  &.route-name {
    min-width: 150px;
    width: 150px;
  }

  &.plate {
    min-width: 100px;
    width: 100px;
  }

  &.document-status {
    min-width: 90px;
    width: 90px;
    text-align: center;
  }
`;

export const DocumentsTab = ({ projectName, vehicles, projectId, isActive, refreshKey }) => {
  const { signedInUserEmail, darkMode, accessToken, setProgress } = useContext(MainContext);
  const [searchTerm, setSearchTerm] = useState('');
  const [rows, setRows] = useState([]);
  const [exRows, setExcelRows] = useState([]);
  const [fontSize, setFontSize] = useState(14);
  const [autoFontSize, setAutoFontSize] = useState(14);
  const [useAutoFontSize, setUseAutoFontSize] = useState(true);
  const [filesStatus, setFilesStatus] = useState(null); // File status data from API
  const [loadingStatus, setLoadingStatus] = useState(false);
  const tableRef = useRef(null);

  const sasToken = localStorage.getItem('sasToken');

  // Driver document keys
  const driverDocKeys = ["ehliyet", "psikoTeknik", "sabikaKaydi", "saglikRaporu"];
  // Vehicle document keys
  const vehicleDocKeys = ["muayene", "ruhsat", "koltukSigortasi", "trafikSigortasi", "emergencyEvacuationPlan"];

  const fileKeys = [...driverDocKeys, ...vehicleDocKeys];

  const typeMapping = {
    "muayene": "Muayene belgesi",
    "ruhsat": "Ruhsat",
    "koltukSigortasi": "Koltuk sigorta belgesi",
    "trafikSigortasi": "Trafik sigorta belgesi",
    "ehliyet": "Ehliyet",
    "psikoTeknik": "Psikoteknik belgesi",
    "sabikaKaydi": "Sabıka kaydı",
    "saglikRaporu": "Sağlık raporu",
    "emergencyEvacuationPlan": "Acil eylem kaçış planı"
  };

  // Function to change font size
  const changeFontSize = useCallback((delta) => {
    setFontSize((prevSize) => {
      const newSize = prevSize + delta;
      return Math.max(10, Math.min(20, newSize));
    });
    setUseAutoFontSize(false);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (tableRef.current && useAutoFontSize) {
        const tableWidth = tableRef.current.offsetWidth;
        const newFontSize = Math.max(10, Math.min(14, tableWidth / 50));
        setAutoFontSize(newFontSize);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, [useAutoFontSize]);

  const effectiveFontSize = useAutoFontSize ? autoFontSize : fontSize;

  const exportToExcel = () => {
    if (rows.length === 0) return;

    const newWs = XLSX.utils.aoa_to_sheet([]);
    const title = `${projectName} Evrak Listesi`;
    XLSX.utils.sheet_add_aoa(newWs, [[title]], { origin: 'A1' });
    XLSX.utils.sheet_add_aoa(newWs, [[""]], { origin: 'A2' });
    newWs['A2'].s = {
        fill: {
            fgColor: { rgb: "ed545c" }
        },
    };

    // Build rows with file status
    const excelData = rows?.map(row => {
      const rowData = {
        'Güzergah': row.name,
        'Plaka': row.plate
      };

      // Add file status columns
      fileKeys.forEach(key => {
        const isDriverDoc = driverDocKeys.includes(key);
        let fileInfo = null;
        if (filesStatus?.vehicles) {
          const vehicleData = filesStatus.vehicles.find(v => v.vehicleId === row.vehicleId);
          if (vehicleData) {
            if (isDriverDoc) {
              fileInfo = vehicleData.driver?.files?.[key];
            } else {
              fileInfo = vehicleData.files?.[key];
            }
          }
        }
        const hasFile = !!fileInfo?.url;
        const status = fileInfo?.status;
        rowData[typeMapping[key]] = '●';
        // Store status for coloring later
        if (!rowData._statuses) rowData._statuses = {};
        rowData._statuses[key] = hasFile ? status : null;
      });

      return rowData;
    });

    // Extract statuses before writing to sheet
    const rowStatuses = excelData.map(row => {
      const statuses = row._statuses;
      delete row._statuses;
      return statuses;
    });

    XLSX.utils.sheet_add_json(newWs, excelData, {origin: 'A3'});

    const totalColumns = 2 + fileKeys.length; // Güzergah, Plaka, + file columns

    // Merge cells for the title
    const merge = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: totalColumns - 1 } }
    ];
    if (!newWs['!merges']) newWs['!merges'] = [];
    newWs['!merges'] = newWs['!merges'].concat(merge);

    const merge3 = [
        { s: { r: 1, c: 0 }, e: { r: 1, c: totalColumns - 1 } }
    ];
    if (!newWs['!merges']) newWs['!merges'] = [];
    newWs['!merges'] = newWs['!merges'].concat(merge3);

    // Apply styling to the title
    newWs['A1'].s = {
        font: {
            name: 'Arial',
            sz: 20,
            bold: true,
            color: { rgb: "FFFFFF" }
        },
        fill: {
            fgColor: { rgb: "14345c" }
        },
        alignment: {
            horizontal: 'center',
            vertical: 'center'
        }
    };

    const wStyle = {
        font: {
            name: 'Arial',
            sz: 10,
            color: { rgb: "ffffff" }
        },
        fill: {
            fgColor: { rgb: "43648c" }
        },
        alignment: {
            horizontal: 'center',
            vertical: 'center'
        },
        border: {
            top: { style: 'medium', color: { rgb: "000000" } },
            bottom: { style: 'medium', color: { rgb: "000000" } },
            left: { style: 'medium', color: { rgb: "000000" } },
            right: { style: 'medium', color: { rgb: "000000" } }
        }
    };

    const wStyleH = {
      font: {
          name: 'Arial',
          sz: 10,
          color: { rgb: "ffffff" }
      },
      fill: {
          fgColor: { rgb: "14345c" }
      },
      alignment: {
          horizontal: 'center',
          vertical: 'center'
      },
      border: {
          top: { style: 'medium', color: { rgb: "000000" } },
          bottom: { style: 'medium', color: { rgb: "000000" } },
          left: { style: 'medium', color: { rgb: "000000" } },
          right: { style: 'medium', color: { rgb: "000000" } }
      }
    };

    // Generate column letters dynamically
    const getColumnLetter = (index) => {
      let letter = '';
      while (index >= 0) {
        letter = String.fromCharCode(65 + (index % 26)) + letter;
        index = Math.floor(index / 26) - 1;
      }
      return letter;
    };

    const statusColorMap = {
      valid: "00AA00",
      warning: "FF8C00",
      expired: "DD0000",
    };

    const paint = Array.from({ length: totalColumns }, (_, i) => getColumnLetter(i));
    paint.forEach((el, colIndex) => {
      if (!newWs[`${el}3`]) newWs[`${el}3`] = {};
      newWs[`${el}3`].s = colIndex >= 2 ? {
        ...wStyleH,
        alignment: { horizontal: 'left', vertical: 'center' }
      } : wStyleH;

      for (let i = 4; i < rows.length + 4; i++) {
        if (!newWs[`${el}${i}`]) newWs[`${el}${i}`] = {};

        // Apply colored style for status columns (index >= 2)
        if (colIndex >= 2) {
          const rowIdx = i - 4;
          const fileKeyIdx = colIndex - 2;
          const status = rowStatuses[rowIdx]?.[fileKeys[fileKeyIdx]];
          const fontColor = status ? (statusColorMap[status] || "ffffff") : "888888";

          newWs[`${el}${i}`].s = {
            ...wStyle,
            font: {
              ...wStyle.font,
              sz: 14,
              bold: true,
              color: { rgb: fontColor }
            }
          };
        } else {
          newWs[`${el}${i}`].s = wStyle;
        }
      }
    });

    // Column widths: wider for first two columns, narrower for status columns
    var wscols = [
      { wch: 50 },  // Güzergah
      { wch: 15 },  // Plaka
      ...fileKeys.map(() => ({ wch: 12 }))  // Document status columns
    ];

    newWs['!cols'] = wscols;

    // Legend row - starts from file columns (index 2)
    const legendRow = rows.length + 4;
    const legendItems = [
      { symbol: '●', label: 'Geçerli', color: '00AA00' },
      { symbol: '●', label: 'Süresi yaklaşıyor', color: 'FF8C00' },
      { symbol: '●', label: 'Süresi dolmuş', color: 'DD0000' },
      { symbol: '●', label: 'Evrak yok', color: '888888' }
    ];

    const legendBaseStyle = {
      fill: { fgColor: { rgb: "1a1a2e" } },
      alignment: { horizontal: 'center', vertical: 'center' },
      border: {
        top: { style: 'thin', color: { rgb: "333333" } },
        bottom: { style: 'thin', color: { rgb: "333333" } },
        left: { style: 'thin', color: { rgb: "333333" } },
        right: { style: 'thin', color: { rgb: "333333" } }
      }
    };

    // Style empty cells for first two columns
    for (let c = 0; c < 2; c++) {
      const col = getColumnLetter(c);
      if (!newWs[`${col}${legendRow}`]) newWs[`${col}${legendRow}`] = { v: '' };
      newWs[`${col}${legendRow}`].s = legendBaseStyle;
    }

    // Write legend items starting from column C (index 2)
    legendItems.forEach((item, idx) => {
      const colIdx = 2 + idx;
      const col = getColumnLetter(colIdx);
      const cellRef = `${col}${legendRow}`;
      if (!newWs[cellRef]) newWs[cellRef] = {};
      newWs[cellRef].v = `${item.symbol}  ${item.label}`;
      newWs[cellRef].s = {
        ...legendBaseStyle,
        font: { name: 'Arial', sz: 10, bold: true, color: { rgb: item.color } },
        alignment: { horizontal: 'left', vertical: 'center' }
      };
    });

    // Style remaining cells in legend row
    for (let c = 2 + legendItems.length; c < totalColumns; c++) {
      const col = getColumnLetter(c);
      if (!newWs[`${col}${legendRow}`]) newWs[`${col}${legendRow}`] = { v: '' };
      newWs[`${col}${legendRow}`].s = legendBaseStyle;
    }

    // Created by row - starts from file columns (index 2)
    const createdByRow = legendRow + 1;
    const createdBy = `${signedInUserEmail ?? '-'} tarafından ${new Date().toLocaleDateString()} tarihinde oluşturuldu.`;

    const createdByStyle = {
        font: {
            name: 'Arial',
            sz: 11,
            bold: true,
            color: { rgb: "FFFFFF" }
        },
        fill: {
            fgColor: { rgb: "000000" }
        },
        alignment: {
            horizontal: 'right',
            vertical: 'center'
        }
    };

    // Style empty cells for first two columns
    for (let c = 0; c < 2; c++) {
      const col = getColumnLetter(c);
      if (!newWs[`${col}${createdByRow}`]) newWs[`${col}${createdByRow}`] = { v: '' };
      newWs[`${col}${createdByRow}`].s = createdByStyle;
    }

    // Write text starting from column C
    const createdByCol = getColumnLetter(2);
    XLSX.utils.sheet_add_aoa(newWs, [[createdBy]], { origin: `${createdByCol}${createdByRow}` });

    newWs[`${createdByCol}${createdByRow}`].s = createdByStyle;

    // Merge from column C to last column
    const merge2 = [
      { s: { r: createdByRow - 1, c: 2 }, e: { r: createdByRow - 1, c: totalColumns - 1 } }
    ];
    if (!newWs['!merges']) newWs['!merges'] = [];
    newWs['!merges'] = newWs['!merges'].concat(merge2);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, newWs, 'Ana sayfa');
    XLSX.writeFile(wb, `${projectName}-evrak-listesi.xlsx`);
  };

  const generateDocs = () => {
    try {
      let rows = [];
      vehicles?.filter(el => `${el?.plate} / ${el?.name}`.toLocaleLowerCase('tr')?.includes(searchTerm)).forEach(el => {
          let cols = {
            plate: el.plate,
            name: el.name,
            vehicleId: el.vehicleId,
            driverId: el.driverId,
            id: `${el.plate} / ${el.name}`
          };
          rows.push(cols);
      });

      setExcelRows(rows);
      setRows(rows);
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    generateDocs();
  }, [vehicles, searchTerm]);

  // Track last refresh key to detect manual refresh
  const lastRefreshKeyRef = React.useRef(refreshKey);

  // Fetch file status from new endpoint only when tab is active
  useEffect(() => {
    if (!accessToken || !projectId || !isActive) return;

    const isManualRefresh = refreshKey !== lastRefreshKeyRef.current;
    lastRefreshKeyRef.current = refreshKey;

    // Skip if already loaded and not a manual refresh
    if (filesStatus && !isManualRefresh) return;

    setLoadingStatus(true);
    apiService.get(`customer/project/${projectId}/files-status`, accessToken, setProgress)
      .then(response => {
        if (response.success && response.data) {
          setFilesStatus(response.data);
        }
      })
      .catch(err => console.error('Error fetching files status:', err))
      .finally(() => setLoadingStatus(false));
  }, [accessToken, projectId, setProgress, isActive, refreshKey]);

  // Handle cell click - open document using URL from filesStatus
  const handleCellClick = (row, docKey) => {
    if (!filesStatus?.vehicles) return;

    const isDriverDoc = driverDocKeys.includes(docKey);
    const vehicleData = filesStatus.vehicles.find(v => v.vehicleId === row.vehicleId);

    let fileInfo = null;
    if (vehicleData) {
      if (isDriverDoc) {
        fileInfo = vehicleData.driver?.files?.[docKey];
      } else {
        fileInfo = vehicleData.files?.[docKey];
      }
    }

    if (fileInfo?.url && sasToken) {
      window.open(fileInfo.url + sasToken, '_blank');
    } else {
      alert('Evrak bulunamadı');
    }
  };

  return (
    <div style={{ padding: "20px 0" }}>
      <Row className="mb-3">
        <Col xs={12} sm={6} className="mb-2 mb-sm-0">
          <Form.Control
            type="text"
            placeholder="Plaka veya güzergah ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              background: "rgba(255, 255, 255, 0.05)",
              border: "1px solid rgba(20, 52, 92, 0.5)",
              color: "white"
            }}
          />
        </Col>
        <Col xs={12} sm={6} className="d-flex justify-content-sm-end align-items-center">
          <div style={{ display: 'flex', alignItems: 'center', marginRight: '15px' }}>
            <Button
              size="sm"
              onClick={() => changeFontSize(-1)}
              style={{
                marginRight: '5px',
                background: "rgba(20, 52, 92, 0.2)",
                border: "1px solid rgba(20, 52, 92, 0.5)",
                color: "#5a9bd4"
              }}
            >
              A-
            </Button>
            <span style={{ margin: '0 5px', fontSize: '0.9rem', color: '#5a9bd4' }}>{effectiveFontSize.toFixed(1)}px</span>
            <Button
              size="sm"
              onClick={() => changeFontSize(1)}
              style={{
                background: "rgba(20, 52, 92, 0.2)",
                border: "1px solid rgba(20, 52, 92, 0.5)",
                color: "#5a9bd4"
              }}
            >
              A+
            </Button>
          </div>
          <Button
            onClick={exportToExcel}
            style={{
              background: "linear-gradient(135deg, #14345c 0%, #1e5a8a 100%)",
              border: "none",
              color: "#fff",
              fontWeight: "700",
              boxShadow: "0 4px 15px rgba(20, 52, 92, 0.5)"
            }}
          >
            Excel indir
          </Button>
        </Col>
      </Row>
      <div style={{
        display: 'flex',
        gap: '20px',
        marginBottom: '10px',
        padding: '10px 15px',
        background: 'rgba(20, 52, 92, 0.1)',
        borderRadius: '8px',
        flexWrap: 'wrap'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <FontAwesomeIcon icon={faCircleCheck} color="green" />
          <span style={{ color: '#aaa', fontSize: '0.85rem' }}>Geçerli</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <FontAwesomeIcon icon={faExclamationTriangle} color="orange" />
          <span style={{ color: '#aaa', fontSize: '0.85rem' }}>Süresi yaklaşıyor</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <FontAwesomeIcon icon={faCircleXmark} color="red" />
          <span style={{ color: '#aaa', fontSize: '0.85rem' }}>Süresi dolmuş</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <FontAwesomeIcon icon={faCircleXmark} color="gray" />
          <span style={{ color: '#aaa', fontSize: '0.85rem' }}>Evrak yok</span>
        </div>
      </div>
      <div style={{ overflowX: 'auto', height: 'calc(100vh - 330px)' }}>
        <ResponsiveTable
          ref={tableRef}
          striped
          bordered
          hover
          variant="dark"
          style={{
            marginTop: 10,
            marginBottom: 10,
            fontSize: `${effectiveFontSize}px`,
            borderColor: "rgba(20, 52, 92, 0.4)"
          }}
        >
          <thead style={{
            background: "#000000",
            borderBottom: "2px solid rgba(20, 52, 92, 0.6)"
          }}>
            <tr>
              <TableHeader className="route-name" style={{color: "#5a9bd4", fontWeight: "700"}}>Güzergah</TableHeader>
              <TableHeader className="plate" style={{color: "#5a9bd4", fontWeight: "700"}}>Plaka</TableHeader>
              {fileKeys.map(el => (
                <TableHeader className="document-status" key={el} title={typeMapping[el]} style={{color: "#5a9bd4", fontWeight: "700"}}>{typeMapping[el]}</TableHeader>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={index}>
                <TableCell className="route-name" style={{ fontWeight: '700', fontSize: '1.1em' }} title={row.name}>{row.name}</TableCell>
                <TableCell className="plate">{row.plate}</TableCell>
                {fileKeys.map(key => {
                  const isDriverDoc = driverDocKeys.includes(key);
                  const hasRequiredId = isDriverDoc ? row.driverId : row.vehicleId;

                  // Get file status from API response
                  let fileInfo = null;
                  if (filesStatus?.vehicles) {
                    const vehicleData = filesStatus.vehicles.find(v => v.vehicleId === row.vehicleId);
                    if (vehicleData) {
                      if (isDriverDoc) {
                        fileInfo = vehicleData.driver?.files?.[key];
                      } else {
                        fileInfo = vehicleData.files?.[key];
                      }
                    }
                  }

                  const hasFile = !!fileInfo?.url;
                  const fileStatus = fileInfo?.status; // valid, warning, expired, or null

                  return (
                    <TableCell
                      className="document-status"
                      key={key}
                      style={{
                        backgroundColor: darkMode ? "#2a3a4a" : "#e8f4fc",
                        cursor: hasFile ? 'pointer' : 'default',
                        opacity: hasRequiredId ? 1 : 0.5
                      }}
                      onClick={() => hasFile && handleCellClick(row, key)}
                    >
                      {loadingStatus ? (
                        <Spinner animation="border" size="sm" style={{ color: "#5a9bd4" }} />
                      ) : !hasRequiredId ? (
                        "-"
                      ) : hasFile ? (
                        fileStatus === 'expired' ? (
                          <FontAwesomeIcon icon={faCircleXmark} color="red" />
                        ) : fileStatus === 'warning' ? (
                          <FontAwesomeIcon icon={faExclamationTriangle} color="orange" />
                        ) : (
                          <FontAwesomeIcon icon={faCircleCheck} color="green" />
                        )
                      ) : (
                        <FontAwesomeIcon icon={faCircleXmark} color="gray" />
                      )}
                    </TableCell>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </ResponsiveTable>
      </div>
    </div>
  );
};
